import {
  ApolloClient,
  InMemoryCache,
  DocumentNode,
  FetchPolicy,
} from '@apollo/client/core'
import { ApolloLink } from '@apollo/client/core'
import { HttpLink } from '@apollo/client/link/http'
import { SetContextLink } from '@apollo/client/link/context'
import { ErrorLink } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { GraphQLConfig } from '@/infrastructure/http/config'
import {
  NetworkError,
  UnauthorizedError,
  ValidationError,
} from '@/core/shared/errors'

interface AuthContext extends ApolloLink.OperationContext {
  headers?: Record<string, string>
}

export interface GraphQLClientInterface {
  query<TData = unknown, TVariables = Record<string, unknown>>(options: {
    query: DocumentNode
    variables?: TVariables
    fetchPolicy?: FetchPolicy
  }): Promise<TData>

  mutate<TData = unknown, TVariables = Record<string, unknown>>(options: {
    mutation: DocumentNode
    variables?: TVariables
  }): Promise<TData>
}

export class ApolloGraphQLClient implements GraphQLClientInterface {
  private readonly client: ApolloClient

  constructor(
    config: GraphQLConfig,
    private getToken?: () => string | null
  ) {
    const timeout = config.timeout

    const httpLink = new HttpLink({
      uri: config.uri,
      credentials: config.credentials,
      fetch: async (uri, options) => {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), config.timeout)

        try {
          return await fetch(uri, {
            ...options,
            signal: controller.signal,
          })
        } finally {
          clearTimeout(timeoutId)
        }
      },
    })

    const authLink = new SetContextLink((prevContext: AuthContext) => {
      const token = this.getToken?.()
      return {
        headers: {
          ...(prevContext.headers || {}),
          ...(token && { authorization: `Bearer ${token}` }),
        },
      }
    })

    const retryLink = new RetryLink({
      delay: {
        initial: 1000,
        max: 5000,
        jitter: true,
      },
      attempts: {
        max: 3,
        retryIf: (error) => {
          // Retry on network errors and timeouts, but not on GraphQL errors
          const shouldRetry = error?.name === 'AbortError' ||
                             error?.message?.includes('timeout') ||
                             error?.message?.includes('NetworkError') ||
                             error?.message?.includes('Failed to fetch')

          if (shouldRetry) {
            console.warn(`🔄 Retrying GraphQL request due to: ${error?.message || error?.name}`)
          }

          return shouldRetry
        },
      },
    })

    const errorLink = new ErrorLink(({ error }) => {
      if (CombinedGraphQLErrors.is(error)) {
        error.errors.forEach(({ message, locations, path, extensions }) => {
          console.error(
            `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${JSON.stringify(path)}`
          )

          // Handle specific GraphQL errors based on extensions
          if (extensions?.code === 'UNAUTHENTICATED') {
            throw new UnauthorizedError(message)
          }

          if (extensions?.code === 'BAD_USER_INPUT') {
            throw new ValidationError(message)
          }
        })
      } else {
        console.error(`[Network error]: ${error}`)

        // Handle timeout errors
        if (error.name === 'AbortError') {
          throw new NetworkError(`GraphQL request timed out after ${timeout}ms`)
        }

        if (
          'result' in error &&
          Array.isArray((error as { result?: unknown }).result) &&
          (error as { result: Array<{ extensions?: { code?: string } }> })
            .result[0]?.extensions?.code === 'UNAUTHENTICATED'
        ) {
          throw new UnauthorizedError('Authentication required')
        }

        throw new NetworkError(`GraphQL network error: ${error.message}`)
      }
    })

    this.client = new ApolloClient({
      link: ApolloLink.from([errorLink, retryLink, authLink, httpLink]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          errorPolicy: 'all',
          fetchPolicy: 'no-cache',
        },
        query: {
          errorPolicy: 'all',
          fetchPolicy: 'no-cache',
        },
        mutate: {
          errorPolicy: 'all',
        },
      },
    })
  }

  async query<TData = unknown, TVariables = Record<string, unknown>>(options: {
    query: DocumentNode
    variables?: TVariables
    fetchPolicy?: FetchPolicy
  }): Promise<TData> {
    try {
      const result = await this.client.query({
        query: options.query,
        variables: options.variables as Record<string, unknown>,
        fetchPolicy: options.fetchPolicy,
      })

      return result.data as TData
    } catch (error) {
      console.error('GraphQL Query Error:', error)
      throw error
    }
  }

  async mutate<TData = unknown, TVariables = Record<string, unknown>>(options: {
    mutation: DocumentNode
    variables?: TVariables
  }): Promise<TData> {
    try {
      const result = await this.client.mutate({
        mutation: options.mutation,
        variables: options.variables as Record<string, unknown>,
      })

      return result.data as TData
    } catch (error) {
      console.error('GraphQL Mutation Error:', error)
      throw error
    }
  }

  /**
   * Clear the Apollo cache
   */
  clearCache(): void {
    this.client.clearStore()
  }

  /**
   * Reset the Apollo store
   */
  resetStore(): void {
    this.client.resetStore()
  }

  /**
   * Get the underlying Apollo Client instance
   */
  getApolloClient(): ApolloClient {
    return this.client
  }
}
